import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box, Paper, Typography, TextField, Switch, FormControl, RadioGroup,
  FormControlLabel, Radio, Tooltip, Button, CircularProgress,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PortalPicker from './PortalPicker';
import { WithContext as ReactTags } from 'react-tag-input';
import { useNotification } from '../context/NotificationContext';

const apiPrefix = window.location.hostname === 'localhost' ? '' : '/api';

const KeyCodes = { comma: 188, enter: 13 };
const delimiters = [KeyCodes.comma, KeyCodes.enter];

const SectionHeader = ({ title, tooltip }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1, mt: 4 }}>
    <Typography
      variant="h6"
      sx={{ fontWeight: 600, color: '#2c3e50', borderBottom: '2px solid #0079bf', pb: 0.5, fontSize: '1rem' }}
    >
      {title}
    </Typography>
    {tooltip && (
      <Tooltip title={tooltip} placement="right">
        <HelpOutlineIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
      </Tooltip>
    )}
  </Box>
);

const AlertDetails = () => {
  const { state } = useLocation();
  const { alarm } = state || {};
  const navigate = useNavigate();

  const [alertData, setAlertData] = useState(alarm || {});
  const [initialData, setInitialData] = useState(alarm || {});
  const [isActive, setIsActive] = useState(alarm?.is_active ?? true);
  const [emailTags, setEmailTags] = useState(alarm?.email_addresses ? alarm.email_addresses.map(email => ({ id: email, text: email })) : []);
  const [initialEmailTags, setInitialEmailTags] = useState(alarm?.email_addresses ? alarm.email_addresses.map(email => ({ id: email, text: email })) : []);
  const [termTags, setTermTags] = useState(alarm?.terms ? alarm.terms.map(term => ({ id: term, text: term })) : []);
  const [initialTermTags, setInitialTermTags] = useState(alarm?.terms ? alarm.terms.map(term => ({ id: term, text: term })) : []);
  const [excludingTermTags, setExcludingTermTags] = useState(alarm?.excluding_terms ? alarm.excluding_terms.map(term => ({ id: term, text: term })) : []);
  const [initialExcludingTermTags, setInitialExcludingTermTags] = useState(alarm?.excluding_terms ? alarm.excluding_terms.map(term => ({ id: term, text: term })) : []);
  const [selectedPortals, setSelectedPortals] = useState(alarm?.portal_objects || []);
  const [initialSelectedPortals, setInitialSelectedPortals] = useState(alarm?.portal_objects || []);
  const [notificationMode, setNotificationMode] = useState(alarm?.notification_mode || 'immediate');
  const [initialNotificationMode] = useState(alarm?.notification_mode || 'immediate');
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const { showError, showSuccess } = useNotification();

  useEffect(() => { document.title = 'Uredi alert | Media Scanner'; }, []);

  useEffect(() => {
    setIsActive(alarm?.is_active ?? true);
    setSelectedPortals(alarm?.portal_objects || []);
    setEmailTags(alarm?.email_addresses ? alarm.email_addresses.map(email => ({ id: email, text: email })) : []);
    setTermTags(alarm?.terms ? alarm.terms.map(term => ({ id: term, text: term })) : []);
    setExcludingTermTags(alarm?.excluding_terms ? alarm.excluding_terms.map(term => ({ id: term, text: term })) : []);
    setInitialData(alarm || {});
    setInitialEmailTags(alarm?.email_addresses ? alarm.email_addresses.map(email => ({ id: email, text: email })) : []);
    setInitialTermTags(alarm?.terms ? alarm.terms.map(term => ({ id: term, text: term })) : []);
    setInitialExcludingTermTags(alarm?.excluding_terms ? alarm.excluding_terms.map(term => ({ id: term, text: term })) : []);
    setInitialSelectedPortals(alarm?.portal_objects || []);
  }, [alarm]);

  useEffect(() => {
    const isDataUnchanged =
      alertData.name === initialData.name &&
      notificationMode === initialNotificationMode &&
      JSON.stringify(selectedPortals.map(p => p.id).sort()) === JSON.stringify(initialSelectedPortals.map(p => p.id).sort()) &&
      JSON.stringify(termTags) === JSON.stringify(initialTermTags) &&
      JSON.stringify(emailTags) === JSON.stringify(initialEmailTags) &&
      JSON.stringify(excludingTermTags) === JSON.stringify(initialExcludingTermTags);

    const isFormValid =
      alertData.name && alertData.name.length >= 3 &&
      selectedPortals.length > 0 &&
      termTags.length > 0 &&
      emailTags.length > 0;

    setIsSaveDisabled(isDataUnchanged || !isFormValid);
  }, [alertData, notificationMode, selectedPortals, termTags, emailTags, excludingTermTags, initialData, initialNotificationMode, initialSelectedPortals, initialTermTags, initialEmailTags, initialExcludingTermTags]);

  const handleToggleActive = async (newValue) => {
    setIsActive(newValue);
    try {
      await axios.put(`${apiPrefix}/updateAlert`, {
        id: alertData.id,
        name: alertData.name || '',
        is_active: newValue,
        is_archived: false,
        notification_mode: notificationMode,
      }, { withCredentials: true });
      showSuccess(newValue ? 'Alert aktiviran' : 'Alert deaktiviran');
    } catch (error) {
      setIsActive(!newValue);
      showError('Greška pri promjeni statusa alerta');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updateResponse = await axios.post(
        `${apiPrefix}/updateAlertFull`,
        {
          id: alertData.id,
          name: alertData.name,
          notification_mode: notificationMode,
          list_portal_ids: selectedPortals.map(p => p.id),
          list_term_names: termTags.map(tag => tag.text),
          list_excluding_term_names: excludingTermTags.map(tag => tag.text),
          list_email_addresses: emailTags.map(tag => tag.text),
        },
        { withCredentials: true }
      );

      if (updateResponse.status === 200) {
        showSuccess('Alert uspješno spremljen');
        navigate('/alarms');
      } else {
        throw new Error('Failed to update the alert');
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        window.location.href = '/';
      } else {
        showError('Greška pri spremanju alerta. Pokušajte ponovo.');
      }
    } finally {
      setSaving(false);
    }
  };

  const validationErrors = [];
  if (!alertData.name || alertData.name.length < 3) validationErrors.push('Naziv alerta mora imati najmanje 3 znaka');
  if (selectedPortals.length === 0) validationErrors.push('Odaberite barem jedan portal');
  if (termTags.length === 0) validationErrors.push('Dodajte barem jedan pojam za pretragu');
  if (emailTags.length === 0) validationErrors.push('Dodajte barem jednu email adresu');

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f7fa' }}>
      <Box sx={{ maxWidth: 800, mx: 'auto', px: 2, py: 3 }}>
        {/* Page header */}
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#2c3e50', mb: 3 }}>
          Uredi alert
        </Typography>

        <Paper sx={{ p: 3, borderRadius: 2 }}>
          {/* Name & Status */}
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#2c3e50', mb: 0.5 }}>Naziv</Typography>
          <TextField
            fullWidth
            autoFocus
            value={alertData.name || ''}
            onChange={(e) => setAlertData({ ...alertData, name: e.target.value })}
            error={!alertData.name || alertData.name.length < 3}
            helperText={!alertData.name || alertData.name.length < 3 ? 'Naziv mora imati najmanje 3 znaka' : ''}
            size="small"
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#2c3e50' }}>Status:</Typography>
            <Typography
              variant="caption"
              sx={{
                px: 1, py: 0.25, borderRadius: 1, fontWeight: 600,
                backgroundColor: isActive ? '#e8f5e9' : '#f5f5f5',
                color: isActive ? '#27ae60' : '#999',
              }}
            >
              {isActive ? 'Aktivan' : 'Neaktivan'}
            </Typography>
            <Switch size="small" checked={isActive} onChange={(e) => handleToggleActive(e.target.checked)} color="primary" />
          </Box>

          {/* Notification mode */}
          <SectionHeader title="Način obavještavanja" />
          <FormControl>
            <RadioGroup row value={notificationMode} onChange={(e) => setNotificationMode(e.target.value)}>
              <FormControlLabel value="immediate" control={<Radio />} label="Odmah (svaki pronalazak)" />
              <FormControlLabel value="daily_digest" control={<Radio />} label="Dnevni sažetak (08:30 i 15:30)" />
            </RadioGroup>
          </FormControl>

          {/* Terms */}
          <SectionHeader title="Pojmovi za alert" />
          <ReactTags
            tags={termTags}
            delimiters={delimiters}
            handleDelete={(i) => setTermTags(termTags.filter((_, index) => index !== i))}
            handleAddition={(tag) => setTermTags([...termTags, tag])}
            inputFieldPosition="bottom"
            placeholder="Unesi pojam, zatim zarez ili Enter"
            autocomplete
            editable
          />

          {/* Excluding terms */}
          <SectionHeader
            title="Isključujući pojmovi"
            tooltip="Članci koji sadrže ove pojmove neće biti prikazani u rezultatima pretrage"
          />
          <ReactTags
            tags={excludingTermTags}
            delimiters={delimiters}
            handleDelete={(i) => setExcludingTermTags(excludingTermTags.filter((_, index) => index !== i))}
            handleAddition={(tag) => setExcludingTermTags([...excludingTermTags, tag])}
            inputFieldPosition="bottom"
            placeholder="Unesi pojam za isključivanje, zatim zarez ili Enter"
            autocomplete
            editable
          />

          {/* Portals */}
          <SectionHeader title="Portali" />
          <PortalPicker value={selectedPortals} onChange={setSelectedPortals} showChips={false} autoDetectForeign />

          {/* Email addresses */}
          <SectionHeader title="Email adrese za dostavu alerta" />
          <ReactTags
            tags={emailTags}
            delimiters={delimiters}
            handleDelete={(i) => setEmailTags(emailTags.filter((_, index) => index !== i))}
            handleAddition={(tag) => setEmailTags([...emailTags, tag])}
            inputFieldPosition="bottom"
            placeholder="Unesi email adresu, zatim zarez ili Enter"
            autocomplete
            editable
          />

          {/* Validation errors */}
          {isSaveDisabled && validationErrors.length > 0 && (
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              {validationErrors.join(' • ')}
            </Typography>
          )}

          {/* Action buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 4, pt: 3, borderTop: '1px solid #eee' }}>
            <Button variant="outlined" onClick={() => navigate('/alarms')}>
              Natrag na listu
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={isSaveDisabled || saving}
              startIcon={saving ? <CircularProgress size={16} /> : null}
            >
              {saving ? 'Spremanje...' : 'Spremi promjene'}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default AlertDetails;
